// client/src/pages/LeaderboardPage.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Container
} from '@mui/material';
import { EmojiEvents, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://propertyearn.net/api/api/leaderboard/top-earners', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setLeaderboardData(data);
        } else {
          console.error('Failed to fetch leaderboard data');
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, []);

  // Helper function to safely format numbers
  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined) return '0.00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? defaultValue : num;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiEvents color="primary" sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Top Earners Leaderboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              See how you rank against other task completers
            </Typography>
          </Box>
        </Box>
      </Box>

      {!leaderboardData ? (
        <Alert severity="error">Failed to load leaderboard data</Alert>
      ) : (
        <Box>
          {/* Current User Position */}
          {leaderboardData.user_position && (
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              background: 'linear-gradient(135deg, #137c55ff 0%, #21874dff 100%)',
              color: 'white'
            }}>
              <Typography variant="h6" gutterBottom>
                Your Position
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    #{safeNumber(leaderboardData.user_position.user_rank)}
                  </Typography>
                  <Typography variant="body1">
                    {leaderboardData.user_position.name || 'You'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6">
                    £{safeToFixed(leaderboardData.user_position.total_earnings)}
                  </Typography>
                  <Typography variant="body2">
                    {safeNumber(leaderboardData.user_position.tasks_completed)} tasks completed
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Full Leaderboard */}
          <Paper sx={{ p: 0 }}>
            <List>
              {leaderboardData.top_earners && leaderboardData.top_earners.map((earner, index) => (
                <ListItem 
                  key={earner.id}
                  sx={{ 
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2,
                    backgroundColor: index < 3 ? 
                      (index === 0 ? '#FFF8E1' : 
                       index === 1 ? '#F5F5F5' : 
                       '#FAFAFA') : 'transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {/* Rank */}
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      backgroundColor: 
                        index === 0 ? '#FFD700' :
                        index === 1 ? '#C0C0C0' :
                        index === 2 ? '#CD7F32' : 'primary.main',
                      color: index < 3 ? 'white' : 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      {index + 1}
                    </Box>
                    
                    {/* User Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {earner.name || 'Anonymous'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={earner.tier || 'Basic'} 
                          size="small" 
                          color={
                            earner.tier === 'Pro' ? 'secondary' : 
                            earner.tier === 'Verified' ? 'primary' : 'default'
                          }
                        />
                        <Typography variant="body2" color="text.secondary">
                          {safeNumber(earner.tasks_completed)} tasks • Rating: {safeToFixed(earner.rating, 1)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Earnings */}
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        £{safeToFixed(earner.total_earnings)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Earnings
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>

            {/* Empty State */}
            {(!leaderboardData.top_earners || leaderboardData.top_earners.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Leaderboard Data Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Complete tasks to appear on the leaderboard!
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/tasks')}
                >
                  Browse Available Tasks
                </Button>
              </Box>
            )}
          </Paper>

          {/* Last Updated */}
          {leaderboardData.last_updated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              Last updated: {new Date(leaderboardData.last_updated).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}
    </Container>
  );
}